import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import { companyInfo } from '../data/mock';
import {
  Building2,
  LogOut,
  Phone,
  Mail,
  Calendar,
  Wrench,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
  Plus,
  ArrowLeft,
  User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {  new: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  contacted: { label: 'Being Reviewed', color: 'bg-brand-grey text-brand-navy', icon: Clock },
  assigned: { label: 'Assigned', color: 'bg-purple-100 text-purple-700', icon: User },
  quoted: { label: 'Quote Sent', color: 'bg-orange-100 text-orange-700', icon: FileText },
  booked: { label: 'Booked In', color: 'bg-emerald-100 text-emerald-700', icon: Calendar },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-100 text-brand-slate', icon: CheckCircle }
};

const statusProgress = {
  new: '15%',
  contacted: '30%',
  assigned: '45%',
  quoted: '60%',
  booked: '80%',
  completed: '100%'
};

const tradeLabels = {
  handyman: 'Handyman',
  carpenter: 'Carpentry',
  painter: 'Painting & Decorating',
  plumber: 'Plumbing',
  electrician: 'Electrical',
  plasterer: 'Plastering',
  cleaner: 'Cleaning',
  general: 'General Maintenance'
};

const MemberDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to navigate to contact form
  const goToContactForm = () => {
    window.location.href = '/?scrollTo=contact';
  };

  const fetchEnquiries = useCallback(async () => {
    if (!user?.email) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/member/enquiries`, {
        credentials: 'include',
        headers: {
          'X-User-Email': user.email
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEnquiries(data.enquiries || []);
      } else {
        setError('Failed to load enquiries');
      }
    } catch (err) {
      // Log error in development for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching enquiries:', err.message);
      }
      setError('Failed to load enquiries');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    // Wait for auth check to complete
    if (authLoading) return;
    
    // If not logged in, redirect to home
    if (!user) {
      navigate('/');
      return;
    }

    // Fetch enquiries for logged in user
    fetchEnquiries();
  }, [user, authLoading, navigate, fetchEnquiries]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-grey">
        <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-grey">
      {/* Header */}
      <Header variant="dashboard" />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy">
            Welcome back, {user.name?.split(' ')[0]}!
          </h1>
          <p className="text-brand-slate mt-2">
            Track your maintenance enquiries and their progress.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-brand-navy">{enquiries.length}</div>
              <div className="text-sm text-brand-slate/80">Total Enquiries</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-blue-600">
                {enquiries.filter(e => e.status === 'new').length}
              </div>
              <div className="text-sm text-brand-slate/80">Pending</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-600">
                {enquiries.filter(e => ['booked', 'assigned', 'quoted'].includes(e.status)).length}
              </div>
              <div className="text-sm text-brand-slate/80">In Progress</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-green-600">
                {enquiries.filter(e => e.status === 'completed').length}
              </div>
              <div className="text-sm text-brand-slate/80">Completed</div>
            </CardContent>
          </Card>
        </div>

        {/* New Enquiry CTA */}
        <Card className="mb-8 bg-gradient-to-r from-emerald-500 to-teal-600 border-0">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white text-center sm:text-left">
              <h3 className="text-lg font-semibold">Need maintenance work done?</h3>
              <p className="text-emerald-100 text-sm">Submit a new enquiry and we'll get back to you within 24 hours.</p>
            </div>
            <Button onClick={goToContactForm} className="bg-white text-emerald-600 hover:bg-emerald-50 shrink-0">
              <Plus className="w-4 h-4 mr-2" />
              New Enquiry
            </Button>
          </CardContent>
        </Card>

        {/* Enquiries List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Enquiries</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center">
                <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-brand-slate/80">Loading enquiries...</p>
              </div>
            ) : error ? (
              <div className="py-8 text-center text-red-500">
                {error}
              </div>
            ) : enquiries.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-brand-navy mb-2">No enquiries yet</h3>
                <p className="text-brand-slate/80 mb-4">Submit your first maintenance enquiry to get started.</p>
                <Button onClick={goToContactForm} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Submit Enquiry
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {enquiries.map((enquiry) => {
                  const status = statusConfig[enquiry.status] || statusConfig.new;
                  const StatusIcon = status.icon;
                  
                  return (
                    <div
                      key={enquiry.id}
                      className="border border-slate-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {formatDate(enquiry.created_at)}
                            </span>
                          </div>
                          <h4 className="font-medium text-brand-navy">
                            {tradeLabels[enquiry.trade_type] || enquiry.trade_type}
                            {enquiry.service_name && ` - ${enquiry.service_name}`}
                          </h4>
                          {enquiry.job_description && (
                            <p className="text-sm text-brand-slate mt-1 line-clamp-2">
                              {enquiry.job_description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-brand-slate/80">
                          {enquiry.address && (
                            <span className="hidden md:block">{enquiry.address}</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress indicator */}
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-emerald-500 rounded-full transition-all"
                              style={{ width: statusProgress[enquiry.status] || '100%' }}
                            />
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                          <span>Submitted</span>
                          <span>In Progress</span>
                          <span>Completed</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-8 text-center text-brand-slate">
          <p className="mb-2">Questions about your enquiries?</p>
          <div className="flex items-center justify-center gap-4">
            <a href="tel:07300405045" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700">
              <Phone className="w-4 h-4" />
              07300 405045
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 text-brand-yellow hover:text-yellow-300">
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MemberDashboard;
